import axios, { AxiosResponse } from "axios";
import { State } from "..";

import { BASE_URL } from "server";

export interface CreationResponse {
  status: number;
  message: string;
}

export async function createUser(state: State): Promise<CreationResponse> {
  try {
    // Fonction pour mapper les types d'interface utilisateur vers les types d'API
    const mapUserTypeToApiType = (uiType: string): string => {
      switch (uiType) {
        case "admin-startup":
          return "admin";
        case "admin-school":
          return "admin_school";
        case "inspecteur":
          return "inspecteur";
        default:
          return uiType; // student, teacher, parent restent identiques
      }
    };

    // Mapper le type d'utilisateur pour l'API
    const apiUserType = mapUserTypeToApiType(
      state.accountCreationState.userType,
    );

    switch (state.accountCreationState.userType) {
      case "student":
        console.log("Sending student data to server");
        const studentData = {
          ...state.accountCreationState.StudentInfo,
          userRole: apiUserType,
        };
        return await createStudent(studentData);
      case "teacher":
        const teacherData = {
          ...state.accountCreationState.TeacherInfo,
          userRole: apiUserType,
        };
        return await createTeacher(teacherData);
      case "admin-school":
        const adminSchoolData = {
          ...state.accountCreationState.AdminSchoolInfo,
          userRole: apiUserType,
        };
        return await createAdminSchool(adminSchoolData);
      case "admin-startup":
        const adminStartupData = {
          ...state.accountCreationState.AdminStartupInfo,
          userRole: apiUserType,
        };
        return await createAdminStartup(adminStartupData);
      case "parent":
        const parentData = {
          ...state.accountCreationState.ParentInfo,
          userRole: apiUserType,
        };
        return await createParent(parentData);
      case "inspecteur":
        const inspectorData = {
          ...state.accountCreationState.InspectorInfo,
          userRole: apiUserType,
        };
        return await createInspector(inspectorData);
      default:
        console.log("Unknown user type:", state.accountCreationState.userType);
        return { status: 400, message: "Type d'utilisateur inconnu" };
    }
  } catch (error) {
    console.log("Error creating user:", error);
    return { status: 500, message: "Internal Server Error" };
  }
}

async function createStudent(studentInfo: any): Promise<CreationResponse> {
  const url = `${BASE_URL}/students/create`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(studentInfo),
      credentials: "include",
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      console.log("server tenté, data :", responseData);

      // Messages d'erreur plus informatifs
      let errorMessage =
        responseData.message || "Erreur lors de la création de l'étudiant";

      if (response.status === 400) {
        errorMessage =
          "Données invalides. Vérifiez que tous les champs obligatoires sont remplis.";
      } else if (response.status === 409) {
        errorMessage =
          "Un étudiant avec ce matricule ou cet email existe déjà.";
      } else if (response.status === 500) {
        errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
      }

      return {
        status: responseData.statusCode || response.status,
        message: errorMessage,
      };
    } else {
      return {
        status: response.status,
        message: "Étudiant créé avec succès !",
      };
    }
  } catch (error) {
    console.error("Error creating student:", error);
    return { status: 500, message: "Internal Server Error" };
  }
}

async function createTeacher(teacherInfo: any): Promise<CreationResponse> {
  const url = `${BASE_URL}/teachers/create`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(teacherInfo),
      credentials: "include",
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);

      let errorMessage =
        responseData.message || "Erreur lors de la création de l'enseignant";

      if (response.status === 400) {
        errorMessage =
          "Données invalides. Vérifiez que tous les champs obligatoires sont remplis.";
      } else if (response.status === 409) {
        errorMessage = "Un enseignant avec cet email existe déjà.";
      } else if (response.status === 500) {
        errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
      }

      return { status: response.status, message: errorMessage };
    }

    return {
      status: response.status,
      message: "Enseignant créé avec succès !",
    };
  } catch (error) {
    console.error("Error creating teacher:", error);
    return { status: 500, message: "Erreur de connexion au serveur" };
  }
}

async function createAdminSchool(
  adminSchoolInfo: any,
): Promise<CreationResponse> {
  const url = `${BASE_URL}/school-admins/create`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(adminSchoolInfo),
      credentials: "include",
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);

      let errorMessage =
        responseData.message ||
        "Erreur lors de la création de l'administrateur d'école";

      if (response.status === 400) {
        errorMessage =
          "Données invalides. Vérifiez que tous les champs obligatoires sont remplis.";
      } else if (response.status === 409) {
        errorMessage = "Un administrateur avec cet email existe déjà.";
      } else if (response.status === 500) {
        errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
      }

      return { status: response.status, message: errorMessage };
    }

    return {
      status: response.status,
      message: "Administrateur d'école créé avec succès !",
    };
  } catch (error) {
    console.error("Error creating admin school:", error);
    return { status: 500, message: "Erreur de connexion au serveur" };
  }
}

async function createAdminStartup(
  adminStartupInfo: any,
): Promise<CreationResponse> {
  const url = `${BASE_URL}/startup-admins/create`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(adminStartupInfo),
      credentials: "include",
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);

      let errorMessage =
        responseData.message ||
        "Erreur lors de la création de l'administrateur startup";

      if (response.status === 400) {
        errorMessage =
          "Données invalides. Vérifiez que tous les champs obligatoires sont remplis.";
      } else if (response.status === 409) {
        errorMessage = "Un administrateur avec cet email existe déjà.";
      } else if (response.status === 500) {
        errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
      }

      return { status: response.status, message: errorMessage };
    }

    return {
      status: response.status,
      message: "Administrateur startup créé avec succès !",
    };
  } catch (error) {
    console.error("Error creating admin startup:", error);
    return { status: 500, message: "Erreur de connexion au serveur" };
  }
}

async function createParent(parentInfo: any): Promise<CreationResponse> {
  const url = `${BASE_URL}/parent/create`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(parentInfo),
      credentials: "include",
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);

      let errorMessage =
        responseData.message || "Erreur lors de la création du parent";

      if (response.status === 400) {
        errorMessage =
          "Données invalides. Vérifiez que tous les champs obligatoires sont remplis.";
      } else if (response.status === 409) {
        errorMessage = "Un parent avec cet email existe déjà.";
      } else if (response.status === 500) {
        errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
      }

      return { status: response.status, message: errorMessage };
    }

    return { status: response.status, message: "Parent créé avec succès !" };
  } catch (error) {
    console.error("Error creating parent:", error);
    return { status: 500, message: "Erreur de connexion au serveur" };
  }
}

async function createInspector(inspectorInfo: any): Promise<CreationResponse> {
  const url = `${BASE_URL}/inspectors/create`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(inspectorInfo),
      credentials: "include",
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);

      let errorMessage =
        responseData.message || "Erreur lors de la création de l'inspecteur";

      if (response.status === 400) {
        errorMessage =
          "Données invalides. Vérifiez que tous les champs obligatoires sont remplis.";
      } else if (response.status === 409) {
        errorMessage = "Un inspecteur avec cet email existe déjà.";
      } else if (response.status === 500) {
        errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
      }

      return { status: response.status, message: errorMessage };
    }

    return {
      status: response.status,
      message: "Inspecteur créé avec succès !",
    };
  } catch (error) {
    console.error("Error creating inspector:", error);
    return { status: 500, message: "Erreur de connexion au serveur" };
  }
}
